import React, { useCallback, useEffect, useState, createContext, useContext, lazy } from 'react';
import { warmCheckoutConnection, originFromUrl } from './connectionWarmer';
import { appendAttribution } from './metaPixel';
interface UpsellProduct {
  id: string;
  name: string;
  variantId: string;
  offerPrice: number;
  benefit: string;
  image: string;
}
interface CheckoutContextType {
  openUpsell: () => void;
  warmConnection: () => void;
}
const CheckoutContext = createContext<CheckoutContextType | null>(null);
interface CheckoutProviderProps {
  children: ReactNode;
  cartBaseUrl: string;
  mainVariantId: string;
  mainPrice: number;
  upsellProducts: UpsellProduct[];
  fullPackVariantId: string;
  fullPackPrice: number;
  fullPackSeparateValue: number;
}
export function CheckoutProvider({
  children,
  cartBaseUrl,
  mainVariantId,
  mainPrice,
  upsellProducts,
  fullPackVariantId,
  fullPackPrice,
  fullPackSeparateValue
}: CheckoutProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  // Origin of the checkout domain, derived from the cart base URL.
  const checkoutOrigin = originFromUrl(cartBaseUrl);
  // Warms DNS + TCP + TLS to the checkout domain (idempotent + throttled).
  const warmConnection = useCallback(() => {
    warmCheckoutConnection(checkoutOrigin);
  }, [checkoutOrigin]);
  const openUpsell = useCallback(() => {
    // Opening the upsell modal is a strong purchase-intent signal.
    warmCheckoutConnection(checkoutOrigin);
    setIsOpen(true);
  }, [checkoutOrigin]);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);
  return <CheckoutContext.Provider value={{
    openUpsell,
    warmConnection
  }} data-id="element-40">
      {children}
      {isOpen && <UpsellModal onClose={close} cartBaseUrl={cartBaseUrl} mainVariantId={mainVariantId} mainPrice={mainPrice} upsellProducts={upsellProducts} fullPackVariantId={fullPackVariantId} fullPackPrice={fullPackPrice} fullPackSeparateValue={fullPackSeparateValue} data-id="element-41" />}
    </CheckoutContext.Provider>;
}
export function useCheckout() {
  const ctx = useContext(CheckoutContext);
  if (!ctx) throw new Error('useCheckout must be used within CheckoutProvider');
  return ctx;
}
function formatPrice(n: number) {
  return '$' + n.toLocaleString('es-AR');
}
interface UpsellModalProps {
  onClose: () => void;
  cartBaseUrl: string;
  mainVariantId: string;
  mainPrice: number;
  upsellProducts: UpsellProduct[];
  fullPackVariantId: string;
  fullPackPrice: number;
  fullPackSeparateValue: number;
}
type LoadingState = null | 'individual' | 'pack' | 'main-only';
function UpsellModal({
  onClose,
  cartBaseUrl,
  mainVariantId,
  mainPrice,
  upsellProducts,
  fullPackVariantId,
  fullPackPrice,
  fullPackSeparateValue
}: UpsellModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<LoadingState>(null);
  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  const toggle = useCallback((id: string) => {
    if (loading) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  }, [loading]);
  const selectedUpsells = upsellProducts.filter(p => selectedIds.has(p.id));
  const upsellsTotal = selectedUpsells.reduce((sum, p) => sum + p.offerPrice, 0);
  const grandTotal = mainPrice + upsellsTotal;
  const hasSelection = selectedUpsells.length > 0;
  const buildCheckoutUrl = (variantIds: string[]) => {
    const items = [`${mainVariantId}:1`, ...variantIds.map(id => `${id}:1`)];
    // Preserve fbclid + UTM attribution toward the Shopify checkout.
    return appendAttribution(`${cartBaseUrl}${items.join(',')}`);
  };
  const goToIndividualCheckout = () => {
    if (loading) return;
    setLoading('individual');
    window.location.href = buildCheckoutUrl(selectedUpsells.map(p => p.variantId));
  };
  const goToFullPackCheckout = () => {
    if (loading) return;
    setLoading('pack');
    window.location.href = buildCheckoutUrl([fullPackVariantId]);
  };
  const goToMainOnlyCheckout = () => {
    if (loading) return;
    setLoading('main-only');
    window.location.href = buildCheckoutUrl([]);
  };
  const handleClose = () => {
    if (loading) return;
    onClose();
  };
  return <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/70 backdrop-blur-sm animate-fadeIn font-sans" onClick={e => {
    if (e.target === e.currentTarget) handleClose();
  }} role="dialog" aria-modal="true" aria-labelledby="upsell-title" data-id="element-42">
      <div className="relative w-full max-w-lg bg-brand-black rounded-t-2xl sm:rounded-2xl shadow-2xl shadow-brand-gold/10 animate-slideUp flex flex-col h-[96vh] sm:h-auto sm:max-h-[90vh] overflow-hidden" data-id="element-43">
        {/* Close button (floating) */}
        <button onClick={handleClose} disabled={loading !== null} aria-label="Cerrar" className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center rounded-full text-brand-white bg-black/60 backdrop-blur-sm border border-brand-gold/30 hover:bg-black/80 hover:border-brand-gold/60 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-44">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-45">
            <line x1="18" y1="6" x2="6" y2="18" data-id="element-46"></line>
            <line x1="6" y1="6" x2="18" y2="18" data-id="element-47"></line>
          </svg>
        </button>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 pt-5 pb-5 sm:pt-6 sm:pb-6 space-y-4 sm:space-y-5" data-id="element-48">
          {/* PACK COMPLETO HIGHLIGHTED CARD */}
          <div className="relative rounded-xl overflow-hidden border-2 border-brand-gold bg-dark-card shadow-lg shadow-brand-gold/20" data-id="element-49">
            <div className="animate-gold-shine absolute top-0 left-0 right-0 text-brand-black text-[11px] sm:text-xs py-2 px-3 text-center" style={{
            fontWeight: 700,
            letterSpacing: '0.18em'
          }} data-id="element-50">
              PACK MÁS ELEGIDO
            </div>

            <div className="pt-11 pb-5 px-4 sm:px-6" data-id="element-51">
              <h3 className="font-serif text-base sm:text-xl text-brand-white" style={{
              fontWeight: 700,
              lineHeight: 1.2,
              letterSpacing: '-0.01em'
            }} data-id="element-52">
                Completá tu bienestar absoluto por solo{' '}
                <span className="text-brand-gold" data-id="element-53">$9.990</span>
              </h3>
              <p className="text-xs sm:text-sm text-brand-gray mt-2.5" style={{
              fontWeight: 400,
              lineHeight: 1.5
            }} data-id="element-54">
                Las 5 guías más poderosas sobre autoestima, manifestación, amor
                propio y crecimiento personal reunidas en una sola colección.
              </p>

              {/* Thumbnail strip of all 5 covers */}
              <div className="mt-3 grid grid-cols-5 gap-1.5" data-id="element-55">
                {upsellProducts.map(p => <div key={p.id} className="relative aspect-[3/4] rounded-md overflow-hidden bg-brand-black border border-brand-gold/30 shadow-sm" data-id="element-56">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" data-id="element-57" />
                  </div>)}
              </div>

              <ul className="mt-3 space-y-1" data-id="element-58">
                {upsellProducts.map(p => <li key={p.id} className="flex items-center gap-2 text-xs sm:text-sm text-brand-white/90" data-id="element-59">
                    <span className="text-brand-gold font-bold" data-id="element-60">✓</span>
                    <span data-id="element-61">{p.name}</span>
                  </li>)}
              </ul>

              <div className="mt-3 flex items-baseline gap-2" data-id="element-62">
                <span className="text-xs text-brand-gray line-through" data-id="element-63">
                  {formatPrice(fullPackSeparateValue)}
                </span>
                <span className="text-2xl sm:text-3xl font-extrabold text-brand-gold leading-none" data-id="element-64">
                  {formatPrice(fullPackPrice)}
                </span>
                <span className="text-[10px] sm:text-xs bg-brand-gold text-brand-black px-2 py-0.5 rounded-full font-bold" data-id="element-65">
                  -50%
                </span>
              </div>

              <button onClick={goToFullPackCheckout} disabled={loading !== null} className="mt-3 w-full bg-brand-gold text-brand-black px-4 py-3 rounded-lg text-sm sm:text-base font-bold hover:opacity-90 transition-all hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2" data-id="element-66">
                {loading === 'pack' ? <>
                    <Spinner data-id="element-67" /> Procesando...
                  </> : 'Sí, quiero los 5 ebooks por $9.990'}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 pt-1" data-id="element-68">
            <div className="flex-1 h-px bg-brand-gold/15" data-id="element-69"></div>
            <span className="text-[11px] sm:text-xs text-brand-gray font-medium uppercase tracking-wider" data-id="element-70">
              O elegí individualmente
            </span>
            <div className="flex-1 h-px bg-brand-gold/15" data-id="element-71"></div>
          </div>

          {/* INDIVIDUAL UPSELLS LIST */}
          <ul className="space-y-2" data-id="element-72">
            {upsellProducts.map(product => {
            const selected = selectedIds.has(product.id);
            return <li key={product.id} data-id="element-73">
                  <button type="button" onClick={() => toggle(product.id)} disabled={loading !== null} aria-pressed={selected} className={`w-full text-left flex items-start gap-3 p-3 sm:p-3.5 rounded-xl border-2 transition-all ${selected ? 'border-brand-gold bg-brand-gold/5 shadow-sm' : 'border-brand-gold/20 bg-dark-card hover:border-brand-gold/50'} disabled:opacity-60 disabled:cursor-not-allowed`} data-id="element-74">
                    {/* Checkbox */}
                    <span className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors ${selected ? 'border-brand-gold bg-brand-gold' : 'border-brand-gold/40 bg-transparent'}`} data-id="element-75">
                      {selected && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#050505" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-76">
                          <polyline points="20 6 9 17 4 12" data-id="element-77"></polyline>
                        </svg>}
                    </span>

                    {/* Cover image */}
                    <div className="flex-shrink-0 w-14 sm:w-16 aspect-[3/4] rounded-md overflow-hidden bg-brand-black border border-brand-gold/20" data-id="element-78">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" loading="lazy" data-id="element-79" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0" data-id="element-80">
                      <div className="flex items-start justify-between gap-2 flex-wrap" data-id="element-81">
                        <h4 className="font-bold text-sm sm:text-base text-brand-white leading-tight" data-id="element-82">
                          {product.name}
                        </h4>
                        <span className="text-[10px] sm:text-xs bg-brand-gold/15 text-brand-gold px-2 py-0.5 rounded-full font-bold whitespace-nowrap uppercase tracking-wide" data-id="element-83">
                          Oferta especial
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-brand-gray mt-1 leading-snug" data-id="element-84">
                        {product.benefit}
                      </p>
                      <div className="mt-1.5 flex items-baseline gap-1.5 flex-wrap" data-id="element-85">
                        <span className="text-xs sm:text-sm text-brand-gray" data-id="element-86">
                          Hoy:
                        </span>
                        <span className="text-base sm:text-lg font-bold text-brand-gold" data-id="element-87">
                          {formatPrice(product.offerPrice)}
                        </span>
                        <span className="text-xs sm:text-sm text-brand-gray/60 line-through" data-id="element-88">
                          {formatPrice(19990)}
                        </span>
                      </div>
                    </div>
                  </button>
                </li>;
          })}
          </ul>

          <p className="text-[10px] sm:text-[11px] text-brand-gray text-center pt-1 italic" data-id="element-89">
            Se agrega automáticamente a tu compra y recibís todo junto en
            formato digital.
          </p>
        </div>

        {/* FIXED BOTTOM BAR */}
        <div className="flex-shrink-0 border-t border-brand-gold/15 bg-brand-black px-5 sm:px-6 pt-3 pb-4 sm:pb-5" data-id="element-90">
          {hasSelection && <div className="mb-2.5 space-y-1" data-id="element-91">
              <div className="flex items-center justify-between text-xs sm:text-sm text-brand-gray" data-id="element-92">
                <span data-id="element-93">Complementos seleccionados:</span>
                <span className="font-semibold text-brand-white" data-id="element-94">
                  {formatPrice(upsellsTotal)}
                </span>
              </div>
              <div className="flex items-center justify-between text-base sm:text-lg" data-id="element-95">
                <span className="font-semibold text-brand-white" data-id="element-96">
                  Total de tu compra:
                </span>
                <span className="font-extrabold text-brand-gold text-lg sm:text-xl" data-id="element-97">
                  {formatPrice(grandTotal)}
                </span>
              </div>
            </div>}

          <button onClick={hasSelection ? goToIndividualCheckout : goToMainOnlyCheckout} disabled={loading !== null} className="w-full px-6 py-4 rounded-lg text-base sm:text-lg font-bold transition-all hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2 bg-brand-gold text-brand-black hover:opacity-90" data-id="element-98">
            {loading === 'individual' || loading === 'main-only' ? <>
                <Spinner data-id="element-99" /> Procesando...
              </> : hasSelection ? `Agregar a mi pedido por ${formatPrice(upsellsTotal)}` : 'Continuar solo con mi compra'}
          </button>

          <button onClick={goToMainOnlyCheckout} disabled={loading !== null} className="w-full mt-2 text-center text-sm sm:text-base font-semibold text-brand-white bg-white/5 hover:bg-white/10 border border-brand-gold/30 hover:border-brand-gold/50 rounded-lg py-2.5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-100">
            Seguir sin agregar
          </button>
        </div>
      </div>
    </div>;
}
function Spinner() {
  return <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" data-id="element-101">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" data-id="element-102"></circle>
      <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-103"></path>
    </svg>;
}